/**
 * Recursively extracts all error paths from a nested error object
 * 
 * @param errors - Formik error
 * @param prefix - Internal use only: prefix for nested paths (used in recursion)
 * 
 * @returns Array of dot-notation paths to all error locations in the object

 * @remarks
 * - Handles nested objects and arrays
 * - Special cases for 'to' and 'from' keys (adds them directly without recursion)
 * - Returns paths in dot notation (e.g., 'parent.child.0.field')
 */
export const getErrorPaths = (errors: Record<string, any>, prefix = ''): string[] => {
  return Object.keys(errors).reduce<string[]>((paths, key) => {
    const currentPath = prefix ? `${prefix}.${key}` : key
    const value = errors[key]

    if (key === 'to' || key === 'from') {
      paths.push(currentPath)
      return paths
    }

    if (typeof value === 'object' && value !== null) {
      if (Array.isArray(value)) {
        value.forEach((item, index) => {
          if (item !== null && typeof item === 'object') {
            paths.push(...getErrorPaths(item, `${currentPath}.${index}`))
          }
        })
      } else {
        paths.push(...getErrorPaths(value, currentPath))
      }
    } else {
      paths.push(currentPath)
    }

    return paths
  }, [])
}

const SORT_KEYS_ORDER_VALIDATE = [
  'from',
  'to',
  'parcels',
  'check_weight',
  'ship_by_date',
  'international_option',
  'custom_items',
  'reference1',
  'order_items',
  'signature_confirmation',
  'insurance_option',
]

/**
 * Gets the sort order index for a given path based on predefined key ordering.
 *
 * @param path - A string path where the first segment is used as the lookup key.
 *              Expected format: "key.rest.of.path" or just "key"
 *
 * @returns The index position of the first path segment in the SORT_KEYS_ORDER_VALIDATE array.
 *          Returns -1 if the key is not found in the array.
 *
 * @example
 * // Assuming SORT_KEYS_ORDER_VALIDATE = ['name', 'age', 'date']
 * getKeyOrder('name.first'); // returns 0
 * getKeyOrder('age');       // returns 1
 * getKeyOrder('unknown');   // returns -1
 */
export const getKeyOrder = (path: string) => {
  const key = path.split('.')[0]
  return SORT_KEYS_ORDER_VALIDATE.indexOf(key)
}
